import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import TeacherExams from "@/pages/TeacherExams";
import api, { apiErr, API_BASE } from "@/lib/api";
import { PdfViewer } from "@/components/PdfViewer";
import { MarkdownMessage } from "@/components/MarkdownMessage";
import { UnifiedComposer } from "@/pages/tuition/TuitionChatParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Brain, Languages, BookOpen, ArrowRight, ArrowLeft, Send, Volume2, Loader2, Sparkles, Lightbulb, NotebookPen, Trash2, Check, BookMarked, FileText, Maximize2, Minimize2, Image as ImageIcon, X, ClipboardList, Plus, Send as SendIcon, Save, Printer, RefreshCw, History as HistoryIcon, RotateCcw, Eye, Video, Headphones } from "lucide-react";
import { toast } from "sonner";
import { VideoEmbed } from "@/pages/tuition/TuitionChatParts";

function classLabel(c) {
  if (["LKG", "UKG", "Play Group"].includes(c)) return c;
  const num = parseInt(c, 10);
  if (isNaN(num)) return `Class ${c}`;
  return `Class ${num}`;
}

// Mirrors the backend `_paper_language`: language subjects get their guidance in
// that language; everything else defaults to English.
const _SUBJECT_LANGS = [
  ["kannada", "Kannada"], ["hindi", "Hindi"], ["sanskrit", "Sanskrit"],
  ["telugu", "Telugu"], ["tamil", "Tamil"], ["urdu", "Urdu"],
  ["marathi", "Marathi"], ["malayalam", "Malayalam"],
];
function replyLanguageFor(subjectName) {
  const s = (subjectName || "").toLowerCase();
  for (const [key, name] of _SUBJECT_LANGS) if (s.includes(key)) return name;
  return "English";
}

// Categorised one-tap teaching actions. Each sends a well-crafted instruction
// to the lesson-grounded mentor, so voice / diagrams / save-note / PDF all work.
const ACTION_CATEGORIES = [
  {
    key: "prepare", label: "Prepare to Teach", icon: BookOpen,
    actions: [
      { label: "Teach This Topic", prompt: "Coach me on how to teach this lesson to my class, step by step — what to say first, how to build it up, and where students usually get confused." },
      { label: "Explain Concept", prompt: "Explain the core concept of this lesson clearly and correctly, the way I should explain it to my students." },
      { label: "Teaching Notes", prompt: "Create clear, well-structured teaching notes for this lesson — key points, definitions and main ideas in the order I should teach them." },
      { label: "Blackboard Notes", prompt: "Give me concise blackboard notes for this lesson — short points and key terms I can write on the board, in order." },
      { label: "PPT Content", prompt: "Generate slide-by-slide PPT content for this lesson. For each slide give a numbered title and 3–5 short bullet points, copy-paste ready for PowerPoint." },
      { label: "Classroom Examples", prompt: "Give me 4 classroom examples that clearly explain this lesson's main concepts to students." },
      { label: "Real-Life Examples", prompt: "Give me 4 relatable real-life examples for this lesson that my students will connect with." },
      { label: "Classroom Activity", prompt: "Suggest one engaging classroom activity to teach this lesson — include steps, materials and time needed." },
      { label: "Experiment", prompt: "Suggest a simple, safe experiment or demonstration for this lesson — materials, steps and what students should observe." },
      { label: "Story Explanation", prompt: "Explain this lesson's main concept through a short, age-appropriate story." },
      { label: "Diagram / Visual", prompt: "Draw a clearly labelled blackboard diagram that explains this lesson's key concept, with a one-line caption." },
      { label: "Mind Map", prompt: "Create a mind map of this lesson as an indented outline — central topic, main branches and sub-points." },
      { label: "Learning Objectives", prompt: "List clear learning objectives for this lesson — what students should know and be able to do by the end." },
      { label: "Lesson Plan", planner: true },
    ],
  },
  {
    key: "during", label: "During Class", icon: Lightbulb,
    actions: [
      { label: "Explain Simpler", prompt: "Explain that again in a much simpler way for my students." },
      { label: "Explain in Detail", prompt: "Explain that in more detail." },
      { label: "Step-by-Step", prompt: "Explain this step by step." },
      { label: "With Example", prompt: "Explain this with a clear example." },
      { label: "With Analogy", prompt: "Explain this using a simple analogy students will understand." },
      { label: "In Kannada", prompt: "Explain this in Kannada." },
      { label: "In English", prompt: "Explain this in English." },
      { label: "Bilingually", prompt: "Explain this bilingually — first in English, then the same in Kannada." },
      { label: "Another Example", prompt: "Give me another, different example." },
      { label: "Ask Students Qs", prompt: "Give me 5 quick questions I can ask students right now to keep them engaged." },
      { label: "Classroom Quiz", prompt: "Generate a quick 5-question classroom quiz on this lesson, with the answer key." },
      { label: "Discussion Qs", prompt: "Give me 4 open-ended discussion questions for this lesson." },
      { label: "Interesting Facts", prompt: "Share 3 interesting facts about this topic to grab students' attention." },
      { label: "Real-Life Uses", prompt: "Give me real-life applications of this lesson's concept." },
    ],
  },
  {
    key: "understand", label: "Check Understanding", icon: ClipboardList, understanding: true,
    types: [
      { label: "Oral Questions", value: "oral" },
      { label: "Concept-Check", value: "concept-check" },
      { label: "Misconception", value: "misconception" },
      { label: "Short Answer", value: "short answer" },
      { label: "Application", value: "application" },
      { label: "HOTS", value: "higher-order thinking (HOTS)" },
    ],
  },
  {
    key: "visual", label: "Visual Assistant", icon: Sparkles,
    actions: [
      { label: "Visual Explanation", prompt: "Generate a full visual explanation for this lesson's key concept. Include: (1) a short explanation, (2) a clearly labelled blackboard diagram, (3) the formula or key rule if any, (4) a step-by-step worked example, (5) a real-world application, and (6) one practice question for students." },
      { label: "Flowchart", prompt: "Draw a labelled flowchart (as a blackboard diagram) for the process in this lesson, with a one-line caption." },
      { label: "Number Line / Graph", prompt: "Draw a clear labelled number line or graph diagram to illustrate this lesson's concept." },
      { label: "Concept Map", prompt: "Draw a labelled concept map connecting the main ideas of this lesson." },
      { label: "Timeline", prompt: "Draw a labelled timeline diagram for the events/steps in this lesson." },
      { label: "Comparison Table", prompt: "Make a clean Markdown comparison table for the key ideas in this lesson." },
    ],
  },
];
const LESSON_DURATIONS = ["30 min", "40 min", "45 min", "60 min", "Custom"];
const DIFFICULTIES = ["Easy", "Medium", "Hard", "Mixed"];

function plannerPrompt(duration) {
  return `Create a detailed ${duration} lesson plan for this lesson. Include: (1) Lesson Information — topic, duration, learning objectives and required materials; (2) a minute-by-minute Lesson Flow with time blocks scaled to ${duration} (e.g., introduction/warm-up, concept explanation, examples/demonstration, student activity, assessment, summary & homework); (3) a short assessment and a homework task. Use clear headings so I can save and print it.`;
}
function understandingPrompt(typeLabel, difficulty) {
  const diff = difficulty === "Mixed" ? "a mix of easy, medium and hard" : `${difficulty.toLowerCase()}`;
  return `Generate 6 ${typeLabel} questions to check my students' understanding of this lesson, at ${diff} difficulty. Number them and provide a short answer key afterwards.`;
}

function QuickActions({ onRun, disabled }) {
  const [cat, setCat] = useState("prepare");
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [customDur, setCustomDur] = useState("");
  const [understand, setUnderstand] = useState(null); // {typeLabel}
  const [difficulty, setDifficulty] = useState("Medium");
  const active = ACTION_CATEGORIES.find((c) => c.key === cat);

  return (
    <div className="mt-3" data-testid="guidance-quick-actions">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {ACTION_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            data-testid={`guidance-cat-${c.key}`}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${cat === c.key ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}
          >
            <c.icon className="h-3.5 w-3.5" /> {c.label}
          </button>
        ))}
      </div>

      {active.understanding ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-400 mr-1">Difficulty:</span>
            {DIFFICULTIES.map((d) => (
              <button key={d} onClick={() => setDifficulty(d)} data-testid={`guidance-diff-${d}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${difficulty === d ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600" : "border-slate-200 dark:border-slate-700 text-slate-500"}`}>
                {d}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {active.types.map((t) => (
              <button key={t.value} disabled={disabled}
                onClick={() => onRun(understandingPrompt(t.value, difficulty))}
                data-testid={`guidance-understand-${t.value.split(" ")[0]}`}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50 transition-colors">
                {t.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {active.actions.map((a, i) => (
            <button
              key={i}
              disabled={disabled}
              onClick={() => (a.planner ? setPlannerOpen(true) : onRun(a.prompt))}
              data-testid={`guidance-action-${a.label.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
              className="text-xs px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50 transition-colors"
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      <Dialog open={plannerOpen} onOpenChange={setPlannerOpen}>
        <DialogContent data-testid="guidance-planner-dialog">
          <DialogHeader>
            <DialogTitle>Smart Lesson Planner</DialogTitle>
            <DialogDescription>Pick a class duration and I'll build a timed lesson plan you can save & print.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {LESSON_DURATIONS.map((d) => (
              <button key={d}
                onClick={() => {
                  if (d === "Custom") return;
                  setPlannerOpen(false);
                  onRun(plannerPrompt(d));
                }}
                data-testid={`guidance-duration-${d.split(" ")[0]}`}
                className="text-sm px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors">
                {d}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Input placeholder="Custom minutes e.g. 50" value={customDur} onChange={(e) => setCustomDur(e.target.value.replace(/[^0-9]/g, ""))} className="w-44" data-testid="guidance-custom-duration" />
            <Button
              disabled={!customDur}
              onClick={() => { setPlannerOpen(false); onRun(plannerPrompt(`${customDur} min`)); setCustomDur(""); }}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="guidance-custom-duration-go">
              Generate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function TeacherGuidance() {
  const [view, setView] = useState("setup"); // "setup" | "chat"
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [lessonId, setLessonId] = useState("");
  const [langMode, setLangMode] = useState("english");
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teacher/guidance/classes");
        setClasses(data.classes || []);
        if (data.classes?.length) setClassFilter(data.classes[0]);
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Could not load classes");
      } finally { setLoadingClasses(false); }
    })();
  }, []);

  useEffect(() => {
    if (!classFilter) { setSubjects([]); setSubjectId(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/teacher/guidance/subjects", { params: { class_name: classFilter } });
        setSubjects(data || []);
      } catch { setSubjects([]); }
      setSubjectId(""); setLessonId(""); setLessons([]);
    })();
  }, [classFilter]);

  useEffect(() => {
    if (!subjectId) { setLessons([]); setLessonId(""); return; }
    setLoadingLessons(true);
    (async () => {
      try {
        const { data } = await api.get("/teacher/guidance/lessons", { params: { subject_id: subjectId } });
        setLessons(data || []);
      } catch { setLessons([]); }
      finally { setLoadingLessons(false); }
      setLessonId("");
    })();
  }, [subjectId]);

  const activeSubject = useMemo(() => subjects.find((s) => s.id === subjectId), [subjects, subjectId]);
  const activeLesson = useMemo(() => lessons.find((l) => l.id === lessonId), [lessons, lessonId]);

  const startGuidance = () => {
    if (!lessonId) { toast.error("Pick a lesson first."); return; }
    setView("chat");
  };

  if (view === "chat" && activeLesson) {
    return (
      <GuidanceChat
        lesson={activeLesson}
        subjectName={activeSubject?.name || ""}
        className={classFilter}
        langMode={langMode}
        onBack={() => setView("setup")}
      />
    );
  }

  if (view === "notes") {
    return <NotesView onBack={() => setView("setup")} />;
  }

  if (view === "exams") {
    return <TeacherExams onExit={() => setView("setup")} />;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="teacher-guidance-page">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Teacher tools</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
              <Brain className="inline h-7 w-7 text-indigo-500 mr-2 -mt-1" /> Teacher Guidance
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
              Pick a subject and lesson, then ask the AI for more examples, analogies and simple ways to explain the topic to your students.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <LangToggle langMode={langMode} onChange={setLangMode} />
            <Button variant="outline" size="sm" className="rounded-full h-10" onClick={() => setView("exams")} data-testid="guidance-generate-paper-btn">
              <FileText className="h-4 w-4 mr-2" /> Generate question paper
            </Button>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 sm:p-8">
          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Class">
              <Select value={classFilter} onValueChange={setClassFilter} disabled={loadingClasses || !classes.length}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="guidance-class-select">
                  <SelectValue placeholder={loadingClasses ? "Loading…" : "Select a class"} />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`guidance-class-opt-${c}`}>{classLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Subject">
              <Select value={subjectId} onValueChange={setSubjectId} disabled={!subjects.length}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="guidance-subject-select">
                  <SelectValue placeholder={subjects.length ? "Select a subject" : "No subjects"} />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`guidance-subject-opt-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Lesson">
              <Select value={lessonId} onValueChange={setLessonId} disabled={!subjectId || loadingLessons}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="guidance-lesson-select">
                  <SelectValue placeholder={!subjectId ? "Pick a subject first" : loadingLessons ? "Loading…" : (lessons.length ? "Select a lesson" : "No material yet")} />
                </SelectTrigger>
                <SelectContent>
                  {lessons.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`guidance-lesson-opt-${l.id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span>{l.title}</span>
                        {(l.has_video || l.video_url) && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300" data-testid={`guidance-lesson-opt-video-${l.id}`}>
                            <Video className="h-2.5 w-2.5" /> Video
                          </span>
                        )}
                        {l.has_audio && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" data-testid={`guidance-lesson-opt-audio-${l.id}`}>
                            <Headphones className="h-2.5 w-2.5" /> Podcast
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {subjectId && !loadingLessons && lessons.length === 0 && (
            <div className="mt-4 text-sm text-amber-600 dark:text-amber-400" data-testid="guidance-no-lessons">
              No lessons with uploaded material in this subject yet. Ask your admin to upload a lesson PDF in the Tuition Library.
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={startGuidance}
              disabled={!lessonId}
              className="h-12 px-8 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white"
              data-testid="guidance-start-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Start guidance <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setNotesOpen(true)}
              disabled={!lessonId}
              className="h-12 px-6 rounded-full"
              data-testid="guidance-class-notes-btn"
            >
              <ClipboardList className="h-4 w-4 mr-2" /> Class Notes
            </Button>
          </div>
        </div>
      </div>

      {notesOpen && activeLesson && (
        <ClassNotesModal
          lesson={activeLesson}
          subjectName={activeSubject?.name || ""}
          className={classFilter}
          langMode={langMode}
          onClose={() => setNotesOpen(false)}
        />
      )}
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function LangToggle({ langMode, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1" data-testid="guidance-lang-toggle">
      <Languages className="h-4 w-4 text-slate-400 ml-2 mr-1" />
      <button
        onClick={() => onChange("kanglish")}
        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${langMode === "kanglish" ? "bg-indigo-500 text-white" : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}
        data-testid="guidance-lang-kanglish"
      >ಕನ್ನಡ + English</button>
      <button
        onClick={() => onChange("english")}
        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${langMode === "english" ? "bg-indigo-500 text-white" : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}
        data-testid="guidance-lang-english"
      >English</button>
    </div>
  );
}

/**
 * TeacherLessonVideo — plays the school-admin-uploaded lesson video for the
 * teacher. Uses the teacher-scoped signed-token endpoint so we don't inherit
 * the student enrolment gate on the AI-Tutor variant.
 */
function TeacherLessonVideo({ lessonId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/teacher/guidance/lessons/${lessonId}/video-token`);
        if (!alive) return;
        setSrc(`${API_BASE}/tuition/lessons/${lessonId}/video?t=${encodeURIComponent(data.token)}`);
      } catch { if (alive) setErr(true); }
    })();
    return () => { alive = false; };
  }, [lessonId]);
  if (err) return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-4 py-3 text-sm" data-testid="guidance-video-error">
      This video isn't available right now.
    </div>
  );
  if (!src) return (
    <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="guidance-video-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading lesson video…
    </div>
  );
  return (
    <video
      src={src}
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload noplaybackrate noremoteplayback"
      disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()}
      className="rounded-xl border border-slate-200 dark:border-slate-700 w-full max-h-[280px] bg-black"
      data-testid="guidance-lesson-video"
    >
      Your browser does not support inline video.
    </video>
  );
}

/**
 * TeacherLessonAudio — plays the admin-uploaded MP3 podcast for the teacher,
 * mirroring the student LessonAudio component with a teacher-scoped token.
 */
function TeacherLessonAudio({ lessonId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/teacher/guidance/lessons/${lessonId}/audio-token`);
        if (!alive) return;
        setSrc(`${API_BASE}/tuition/lessons/${lessonId}/audio?t=${encodeURIComponent(data.token)}`);
      } catch { if (alive) setErr(true); }
    })();
    return () => { alive = false; };
  }, [lessonId]);
  if (err) return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-3 py-2 text-sm" data-testid="guidance-audio-error">
      Podcast isn't available.
    </div>
  );
  if (!src) return (
    <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="guidance-audio-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading podcast…
    </div>
  );
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 flex items-center gap-2" data-testid="guidance-lesson-audio-wrap">
      <span className="text-[11px] font-semibold text-violet-600 dark:text-violet-300 uppercase tracking-wide">Podcast</span>
      <audio
        src={src}
        controls
        preload="metadata"
        controlsList="nodownload noplaybackrate noremoteplayback"
        onContextMenu={(e) => e.preventDefault()}
        className="flex-1 min-w-0"
        data-testid="guidance-lesson-audio"
      >
        Your browser does not support inline audio.
      </audio>
    </div>
  );
}

/**
 * LessonMedia — renders any uploaded video / external video URL / audio
 * podcast the admin has attached to this lesson. Collapsible so it doesn't
 * fight the PDF for space when the teacher isn't using it.
 */
function LessonMedia({ lesson }) {
  const hasUploadedVideo = !!lesson?.has_video;
  const hasExternalVideo = !!(lesson?.video_url && !hasUploadedVideo);
  const hasAudio = !!lesson?.has_audio;
  const anyMedia = hasUploadedVideo || hasExternalVideo || hasAudio;
  const [open, setOpen] = useState(true);
  if (!anyMedia) return null;
  return (
    <div className="mb-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" data-testid="guidance-lesson-media">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        data-testid="guidance-lesson-media-toggle"
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-slate-400">
          {hasUploadedVideo || hasExternalVideo ? <Video className="h-3.5 w-3.5" /> : <Headphones className="h-3.5 w-3.5" />}
          Lesson media
        </span>
        <div className="flex items-center gap-1.5 text-[11px]">
          {(hasUploadedVideo || hasExternalVideo) && (
            <span className="px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300 font-medium">Video</span>
          )}
          {hasAudio && (
            <span className="px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 font-medium">Podcast</span>
          )}
        </div>
        <span className="ml-auto text-xs text-slate-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid="guidance-lesson-media-body">
          {hasUploadedVideo && <TeacherLessonVideo lessonId={lesson.id} />}
          {hasExternalVideo && <VideoEmbed url={lesson.video_url} />}
          {hasAudio && <TeacherLessonAudio lessonId={lesson.id} />}
        </div>
      )}
    </div>
  );
}

function GuidanceChat({ lesson, subjectName, className, langMode, onBack }) {
  const [messages, setMessages] = useState([]); // {role, content, ttsToken?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [playingIdx, setPlayingIdx] = useState(null);
  const [savedIdx, setSavedIdx] = useState(new Set());
  const [fullscreen, setFullscreen] = useState(false);
  const [attachedImage, setAttachedImage] = useState(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const [followBottom, setFollowBottom] = useState(true);
  const [recording, setRecording] = useState(false);
  const [recLevel, setRecLevel] = useState(0);
  const recRef = useRef({ mr: null, stream: null, chunks: [], ctx: null, raf: 0 });

  async function startRecording() {
    if (recording || sending) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        cancelAnimationFrame(recRef.current.raf);
        try { recRef.current.ctx?.close(); } catch { /* noop */ }
        stream.getTracks().forEach((t) => t.stop());
        setRecLevel(0);
        const blob = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
        if (!blob.size || recRef.current.aborted) return;
        try {
          setSending(true);
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const tok = localStorage.getItem("sa_token");
          const r = await fetch(`${API_BASE}/teacher/guidance/transcribe`, {
            method: "POST", credentials: "include",
            headers: { ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
            body: fd,
          });
          const j = await r.json();
          const text = (j.text || "").trim();
          setSending(false);
          if (text) send(text);
          else toast.error("Didn't catch that. Please try again.");
        } catch {
          setSending(false);
          toast.error("Could not transcribe audio.");
        }
      };
      // Live mic level for the composer waveform.
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setRecLevel(Math.min(avg / 90, 1));
        recRef.current.raf = requestAnimationFrame(tick);
      };
      tick();
      recRef.current = { mr, stream, chunks, ctx, raf: recRef.current.raf, aborted: false };
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied.");
    }
  }

  function stopRecording(abort = false) {
    if (!recording) return;
    recRef.current.aborted = abort;
    try { recRef.current.mr?.stop(); } catch { /* noop */ }
    setRecording(false);
  }

  useEffect(() => {
    // Only auto-follow the streaming text for the teacher's own messages. For the
    // on-load lesson overview (kickoff) we keep the view at the TOP so the teacher
    // can start reading immediately while the rest generates below (no forced
    // scroll-down / scroll-up-to-read).
    if (!followBottom) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending, followBottom]);

  // Kick off the lesson overview on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { send(null, true); }, []);

  const send = async (text, kickoff = false) => {
    if (sending) return;
    // Kickoff: keep the view pinned to the top of the overview while it streams.
    // Teacher messages: follow the reply to the bottom as usual.
    setFollowBottom(!kickoff);
    if (kickoff) scrollRef.current?.scrollTo({ top: 0 });
    const img = kickoff ? null : attachedImage;
    const userMsg = kickoff ? null : { role: "user", content: text, image: img };
    const history = kickoff ? [] : [...messages.map((m) => ({ role: m.role, content: m.content })), { role: "user", content: text }];
    if (userMsg) setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setAttachedImage(null);
    setSending(true);
    // Placeholder assistant bubble we stream tokens into (always the last one).
    let assistantIdx = null;
    setMessages((prev) => {
      assistantIdx = prev.length;
      return [...prev, { role: "assistant", content: "", ttsToken: null, streaming: true }];
    });
    const patchLast = (p) => setMessages((prev) => {
      const c = [...prev];
      for (let i = c.length - 1; i >= 0; i--) {
        if (c[i].role === "assistant") { c[i] = { ...c[i], ...p }; break; }
      }
      return c;
    });
    try {
      const tok = localStorage.getItem("sa_token");
      const resp = await fetch(`${API_BASE}/teacher/guidance/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ lesson_id: lesson.id, messages: history, language_mode: langMode, image: img }),
      });
      if (!resp.ok || !resp.body) {
        let detail = "Could not get a reply";
        try { const j = await resp.json(); detail = apiErr(j.detail) || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "", meta = null, streamErr = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const raw = line.slice(5).trim();
          if (!raw || raw === "[DONE]") continue;
          let obj; try { obj = JSON.parse(raw); } catch { continue; }
          if (obj.delta) { full += obj.delta; patchLast({ content: full }); }
          else if (obj.meta) { meta = obj.meta; }
          else if (obj.error) { streamErr = obj.error; }
        }
      }
      if (streamErr) throw new Error(streamErr);
      const ttsToken = (meta && meta.tts_token) || null;
      patchLast({ ttsToken, streaming: false });
      // Auto-speak replies to the teacher's messages (skip the on-mount kickoff,
      // which has no user gesture and would be blocked by browser autoplay policy).
      if (!kickoff && ttsToken && assistantIdx != null) {
        playTts(assistantIdx, ttsToken, { silentFail: true });
      }
    } catch (e) {
      // Drop the empty placeholder on failure.
      setMessages((prev) => prev.filter((m) => !(m.role === "assistant" && m.streaming && !m.content)));
      toast.error(e.message || "Could not get a reply");
    } finally { setSending(false); }
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large. Max 8 MB."); return; }
    const r = new FileReader();
    r.onload = () => setAttachedImage(r.result);
    r.readAsDataURL(f);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const t = input.trim();
    if (t || attachedImage) send(t);
  };

  const playTts = (idx, token, { silentFail = false } = {}) => {
    if (!token) return;
    const tok = localStorage.getItem("sa_token");
    setPlayingIdx(idx);
    fetch(`${API_BASE}/teacher/guidance/tts?token=${encodeURIComponent(token)}`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then((r) => { if (!r.ok) throw new Error("tts"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.onended = () => setPlayingIdx(null);
          audioRef.current.play().catch(() => setPlayingIdx(null));
        }
      })
      .catch(() => {
        setPlayingIdx(null);
        if (!silentFail) toast.error("Audio not available (token may have expired). Ask again to refresh.");
      });
  };

  const listen = (idx, token) => {
    if (!token) return;
    if (playingIdx === idx && audioRef.current) {
      audioRef.current.pause();
      setPlayingIdx(null);
      return;
    }
    playTts(idx, token);
  };

  const saveNote = async (idx, content) => {
    if (savedIdx.has(idx)) return;
    try {
      await api.post("/teacher/guidance/notes", {
        content,
        lesson_id: lesson.id,
        lesson_title: lesson.title,
        subject: subjectName,
        class_name: className,
      });
      setSavedIdx((prev) => new Set(prev).add(idx));
      toast.success("Saved to My Notes");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save note");
    }
  };

  const chatInner = (
    <>
      <audio ref={audioRef} className="hidden" />
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" className="rounded-full" onClick={onBack} data-testid="guidance-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400">{classLabel(className)} · {subjectName}</div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ fontFamily: "Outfit" }}>{lesson.title}</div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
          <Brain className="h-3 w-3" /> AI mentor
        </span>
        <span
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
          title={`The mentor will reply in ${replyLanguageFor(subjectName)} for this subject`}
          data-testid="guidance-reply-language"
        >
          <Languages className="h-3 w-3" /> {replyLanguageFor(subjectName)} ✓
        </span>
        <Button
          variant="outline" size="sm"
          className="rounded-full h-9 w-9 p-0 shrink-0"
          onClick={() => setFullscreen((v) => !v)}
          title={fullscreen ? "Exit full screen" : "Full screen"}
          data-testid="guidance-fullscreen-toggle"
        >
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1" data-testid="guidance-messages">
        {messages.map((m, i) => (
          <Bubble
            key={i}
            role={m.role}
            content={m.content}
            image={m.image}
            ttsToken={m.ttsToken}
            playing={playingIdx === i}
            onListen={() => listen(i, m.ttsToken)}
            onSaveSketch={(svg, si) => saveNote(`sketch-${i}-${si}`, svg)}
          />
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="guidance-typing">
            <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
          </div>
        )}
      </div>

      {/* Quick teaching actions */}
      <QuickActions onRun={(prompt) => send(prompt)} disabled={sending} />

      <div className="mt-3">
        <UnifiedComposer
          value={input}
          onChange={setInput}
          onSend={() => send(input)}
          busy={sending}
          image={attachedImage}
          onImage={setAttachedImage}
          onClearImage={() => setAttachedImage(null)}
          recording={recording}
          level={recLevel}
          onStartRec={startRecording}
          onStopRec={() => stopRecording(false)}
        />
      </div>
    </>
  );

  const pdfUrl = lesson?.id ? `${API_BASE}/teacher/guidance/lessons/${lesson.id}/pdf` : null;
  const pdfPanel = (
    <div className="hidden lg:flex flex-col flex-1 min-w-0" data-testid="guidance-pdf-panel">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="h-9 w-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
          <BookOpen className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Lesson</div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ fontFamily: "Outfit" }}>{lesson.title}</div>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <LessonMedia lesson={lesson} />
        <div className="flex-1 min-h-0">
          {pdfUrl ? <PdfViewer url={pdfUrl} /> : <div className="p-6 text-sm text-slate-400">No PDF available.</div>}
        </div>
      </div>
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[60] bg-slate-50 dark:bg-slate-900 flex flex-col" data-testid="guidance-chat-fullscreen">
        <div className="w-full flex gap-4 flex-1 min-h-0 px-4 sm:px-6 py-4">
          <div className="flex flex-col flex-1 min-w-0">
            {chatInner}
          </div>
          {pdfPanel}
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="flex gap-4" style={{ height: "calc(100vh - 7rem)" }} data-testid="guidance-chat">
        <div className="flex flex-col flex-1 min-w-0">
          {chatInner}
        </div>
        {pdfPanel}
      </div>
    </Layout>
  );
}

function Bubble({ role, content, image, ttsToken, playing, onListen, onSaveSketch }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${isUser ? "bg-indigo-500 text-white" : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100"}`}>
        {image && (
          <img src={image} alt="attachment" className="mb-2 max-h-48 rounded-xl object-cover" data-testid="guidance-bubble-image" />
        )}
        {isUser ? (
          <div className="whitespace-pre-wrap leading-relaxed text-sm">{content}</div>
        ) : (
          <MarkdownMessage content={content} onSaveSketch={onSaveSketch} />
        )}
        {!isUser && ttsToken && (
          <div className="mt-2 flex items-center gap-4">
            <button
              onClick={onListen}
              className="inline-flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-300 hover:underline"
              data-testid="guidance-listen-btn"
            >
              <Volume2 className={`h-3.5 w-3.5 ${playing ? "animate-pulse" : ""}`} /> {playing ? "Playing…" : "Listen"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function NotesView({ onBack }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teacher/guidance/notes");
      setNotes(data.notes || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load notes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/teacher/guidance/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Note deleted");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete");
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

  const [addingId, setAddingId] = useState(null);
  const hasSketch = (c) => /<svg[\s\S]*?<\/svg>/i.test(c || "");
  const addToClassNotes = async (n) => {
    if (!n.lesson_id) { toast.error("This sketch isn't linked to a lesson, so it can't be added to Class Notes."); return; }
    const m = (n.content || "").match(/<svg[\s\S]*?<\/svg>/i);
    if (!m) { toast.error("No sketch found in this note."); return; }
    setAddingId(n.id);
    try {
      await api.post("/teacher/guidance/class-notes/add-figure", {
        lesson_id: n.lesson_id, svg: m[0],
        caption: `Diagram — ${n.lesson_title || "lesson"}`,
      });
      toast.success(`Added to “${n.lesson_title || "lesson"}” Class Notes (draft) — open Class Notes to publish it to students.`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add to Class Notes");
    } finally { setAddingId(null); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="guidance-notes-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onBack} data-testid="notes-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Teacher tools</div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
              <NotebookPen className="inline h-6 w-6 text-indigo-500 mr-2 -mt-1" /> My Notes
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-12 text-center" data-testid="notes-empty">
            <BookMarked className="h-8 w-8 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <div className="text-slate-900 dark:text-slate-100 font-medium">No saved notes yet</div>
            <div className="text-slate-500 text-sm mt-1">Tap “Save to notes” on any AI guidance reply to build your lesson-prep library.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="notes-list">
            {notes.map((n) => (
              <div key={n.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {n.subject && <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 font-medium">{n.subject}</span>}
                    {n.lesson_title && <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{n.lesson_title}</span>}
                    {n.class_name && <span className="text-slate-400">{classLabel(n.class_name)}</span>}
                    <span className="text-slate-400">· {fmt(n.created_at)}</span>
                  </div>
                  <button onClick={() => remove(n.id)} className="text-slate-400 hover:text-rose-500 shrink-0" title="Delete note" data-testid={`note-delete-${n.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <MarkdownMessage content={n.content} className="text-slate-700 dark:text-slate-200" />
                {hasSketch(n.content) && (
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <Button size="sm" variant="outline" className="rounded-full" disabled={addingId === n.id || !n.lesson_id} onClick={() => addToClassNotes(n)} data-testid={`note-to-classnotes-${n.id}`}>
                      {addingId === n.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5 mr-1.5" />}
                      Add sketch to Class Notes
                    </Button>
                    {!n.lesson_id && <span className="text-xs text-slate-400 ml-2">No lesson linked</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function ClassNotesModal({ lesson, subjectName, className, langMode, onClose }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [answeringIdx, setAnsweringIdx] = useState(null);
  const [preview, setPreview] = useState(false);
  const [notesMessage, setNotesMessage] = useState(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return; // load exactly once (guards StrictMode double-invoke)
    loadedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/teacher/guidance/class-notes/generate", { lesson_id: lesson.id, language_mode: langMode });
        setItems(data.items || []);
        setStatus(data.status || "draft");
        setHistoryCount(data.history_count || 0);
        setNotesMessage(data.message || null);
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Could not load class notes");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const addItem = () => setItems((prev) => [...prev, { question: "", answer: "" }]);

  const answerRow = async (i) => {
    const q = (items[i]?.question || "").trim();
    if (!q) { toast.error("Type the question first."); return; }
    setAnsweringIdx(i);
    try {
      const { data } = await api.post("/teacher/guidance/class-notes/answer", { lesson_id: lesson.id, question: q, language_mode: langMode });
      setItem(i, { answer: data.answer || "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not generate answer"); }
    finally { setAnsweringIdx(null); }
  };

  const [answeringAll, setAnsweringAll] = useState(false);
  const answerAll = async () => {
    const blanks = items.map((it, i) => ({ i, q: (it.question || "").trim() }))
      .filter((r) => r.q && !(items[r.i].answer || "").trim());
    if (!blanks.length) { toast.message("Every question already has an answer."); return; }
    setAnsweringAll(true);
    try {
      const { data } = await api.post("/teacher/guidance/class-notes/answer-batch", { lesson_id: lesson.id, questions: blanks.map((b) => b.q), language_mode: langMode });
      const answers = data.answers || [];
      setItems((prev) => {
        const next = [...prev];
        blanks.forEach((b, k) => { if (answers[k]) next[b.i] = { ...next[b.i], answer: answers[k] }; });
        return next;
      });
      toast.success(`Answered ${blanks.length} question${blanks.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not answer all"); }
    finally { setAnsweringAll(false); }
  };

  const cleanItems = () => items.filter((it) => (it.question || "").trim());

  const save = async () => {
    const payload = cleanItems();
    if (!payload.length) { toast.error("Add at least one question."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/teacher/guidance/class-notes", { lesson_id: lesson.id, items: payload });
      setStatus(data.notes.status);
      toast.success("Class notes saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    const payload = cleanItems();
    if (!payload.length) { toast.error("Add at least one question."); return; }
    setPublishing(true);
    try {
      const { data } = await api.post("/teacher/guidance/class-notes/publish", { lesson_id: lesson.id, items: payload });
      setStatus(data.notes.status);
      setHistoryCount(data.notes.history_count || 0);
      toast.success("Published — students can now see these class notes");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not publish"); }
    finally { setPublishing(false); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/teacher/guidance/class-notes/generate", { lesson_id: lesson.id, language_mode: langMode, force: true });
      setItems(data.items || []);
      setNotesMessage(data.message || null);
      if (data.message && !(data.items || []).length) toast.message(data.message);
      else toast.success(langMode === "kanglish" ? "Regenerated in Kannada" : "Regenerated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not regenerate"); }
    finally { setRegenerating(false); }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/teacher/guidance/class-notes/history", { params: { lesson_id: lesson.id } });
      setVersions(data.versions || []);
    } catch { setVersions([]); }
  };

  const restore = async (index) => {
    try {
      const { data } = await api.post("/teacher/guidance/class-notes/revert", { lesson_id: lesson.id, index });
      setItems(data.notes.items || []);
      setStatus(data.notes.status);
      setHistoryCount(data.notes.history_count || 0);
      setHistoryOpen(false);
      toast.success("Restored that version (published)");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not restore"); }
  };

  const printNotes = () => {
    const rows = cleanItems();
    if (!rows.length) { toast.error("Nothing to print."); return; }
    const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const body = rows.map((it, i) => `<div class="qa"><div class="q">${i + 1}. ${esc(it.question)}</div><div class="a">${esc(it.answer)}</div></div>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Class Notes — ${esc(lesson.title)}</title>
      <style>
        body{font-family:Georgia,'Noto Serif Kannada',serif;color:#111;margin:32px;line-height:1.5}
        h1{font-size:20px;margin:0 0 2px} .sub{color:#555;font-size:13px;margin-bottom:18px}
        .qa{margin:0 0 14px;page-break-inside:avoid} .q{font-weight:700;font-size:14px}
        .a{font-size:14px;margin-top:3px;white-space:pre-wrap}
        @media print{body{margin:16mm}}
      </style></head><body>
      <h1>Class Notes — ${esc(lesson.title)}</h1>
      <div class="sub">${esc(classLabel(className))} · ${esc(subjectName)}</div>
      ${body}
      <script>window.onload=function(){window.print()}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="class-notes-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-indigo-500" /> Class Notes — {lesson.title}
            <span className={`ml-1 text-[11px] px-2 py-0.5 rounded-full font-medium ${status === "published" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`} data-testid="class-notes-status">
              {status === "published" ? "Published" : "Draft"}
            </span>
          </DialogTitle>
          <DialogDescription>{classLabel(className)} · {subjectName} — exercise questions after this lesson. Edit the answers, then publish for students.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 -mt-1">
          <Button variant="outline" size="sm" className="rounded-full h-8" onClick={regenerate} disabled={regenerating || loading} data-testid="class-notes-regenerate">
            {regenerating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Regenerate{langMode === "kanglish" ? " in Kannada" : ""}
          </Button>
          <Button variant="outline" size="sm" className="rounded-full h-8" onClick={answerAll} disabled={answeringAll || loading || preview} data-testid="class-notes-answer-all">
            {answeringAll ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Answer all blanks
          </Button>
          <Button variant="outline" size="sm" className="rounded-full h-8" onClick={printNotes} disabled={loading} data-testid="class-notes-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print handout
          </Button>
          <Button variant="outline" size="sm" className="rounded-full h-8" onClick={openHistory} disabled={loading || !historyCount} data-testid="class-notes-history">
            <HistoryIcon className="h-3.5 w-3.5 mr-1.5" /> History{historyCount ? ` (${historyCount})` : ""}
          </Button>
          <Button variant={preview ? "default" : "outline"} size="sm" className={`rounded-full h-8 ${preview ? "bg-slate-800 text-white" : ""}`} onClick={() => setPreview((v) => !v)} disabled={loading} data-testid="class-notes-preview">
            <Eye className="h-3.5 w-3.5 mr-1.5" /> {preview ? "Edit" : "Preview"}
          </Button>
        </div>

        {historyOpen ? (
          <div className="space-y-2" data-testid="class-notes-history-panel">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Previous published versions</div>
              <Button variant="ghost" size="sm" className="rounded-full h-8" onClick={() => setHistoryOpen(false)}>Back to editor</Button>
            </div>
            {versions.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No earlier versions yet.</div>
            ) : versions.map((v) => (
              <div key={v.index} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 flex items-center gap-3" data-testid={`class-notes-version-${v.index}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{v.count} question{v.count === 1 ? "" : "s"}</div>
                  <div className="text-xs text-slate-400">Saved {(() => { try { return new Date(v.saved_at).toLocaleString(); } catch { return ""; } })()}</div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full h-8" onClick={() => restore(v.index)} data-testid={`class-notes-restore-${v.index}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
                </Button>
              </div>
            ))}
          </div>
        ) : loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500 gap-2" data-testid="class-notes-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Preparing questions &amp; answers…
          </div>
        ) : preview ? (
          <div className="space-y-3" data-testid="class-notes-preview-list">
            {cleanItems().length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Nothing to preview yet.</div>
            ) : cleanItems().map((it, i) => (
              <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4" data-testid={`class-note-preview-${i}`}>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex gap-2">
                  <span className="text-sky-500">{i + 1}.</span><span className="whitespace-pre-wrap">{it.question}</span>
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-300 mt-2 leading-relaxed pl-5">
                  <MarkdownMessage content={it.answer} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="class-notes-list">
            {notesMessage && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 text-sm px-4 py-3" data-testid="class-notes-message">
                {notesMessage}
              </div>
            )}
            {items.map((it, i) => (
              <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid={`class-note-item-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="text-sm font-semibold text-indigo-500 mt-2">{i + 1}.</span>
                  <div className="flex-1 space-y-2">
                    <Textarea value={it.question} onChange={(e) => setItem(i, { question: e.target.value })} placeholder="Question" className="rounded-lg text-sm font-medium min-h-[44px]" data-testid={`class-note-q-${i}`} />
                    <div className="relative">
                      <Textarea value={it.answer} onChange={(e) => setItem(i, { answer: e.target.value })} placeholder="Answer (or click ✨ to auto-answer)" className="rounded-lg text-sm min-h-[64px] pr-10" data-testid={`class-note-a-${i}`} />
                      <button onClick={() => answerRow(i)} disabled={answeringIdx !== null} title="Auto-answer this question with AI" className="absolute top-2 right-2 text-slate-400 hover:text-indigo-500 disabled:opacity-40" data-testid={`class-note-answer-${i}`}>
                        {answeringIdx === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      </button>
                    </div>
                    {/<svg/i.test(it.answer) && (
                      <div className="rounded-lg border border-slate-100 dark:border-slate-700 p-1.5 bg-slate-50 dark:bg-slate-900/40" data-testid={`class-note-svg-preview-${i}`}>
                        <MarkdownMessage content={it.answer} />
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-rose-500 mt-2" title="Remove" data-testid={`class-note-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-full" onClick={addItem} data-testid="class-note-add">
              <Plus className="h-4 w-4 mr-1.5" /> Add question
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
          <Button variant="outline" onClick={save} disabled={saving || loading} className="rounded-full" data-testid="class-notes-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save draft
          </Button>
          <Button onClick={publish} disabled={publishing || loading} className="rounded-full bg-emerald-500 hover:bg-emerald-600" data-testid="class-notes-publish">
            {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SendIcon className="h-4 w-4 mr-2" />} Publish to students
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
